import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Divider } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Visibility, Delete } from '@mui/icons-material';

import { Rooftop } from '@libs/models';
import { useRooftopSearchQuery } from '@carcareers/store';
import {
  DashboardPageContainer,
  Drawer,
  EmployerRooftopCreateForm,
  DataGridV2 as DataGrid,
  RooftopHelpContent,
  InfoButton,
} from '@carcareers/components';
import { useRooftop } from '@carcareers/hooks';
import { FormProvider, useDrawer } from '@carcareers/contexts';
import { ROOFTOP_INDEX_COLUMN_INITIAL_STATE, ROOFTOP_INDEX_COLUMNS } from '@carcareers/utils';

export const EmployerRooftopIndex: FC = () => {
  const navigate = useNavigate();
  const { setOpen } = useDrawer();
  const { deleteRooftop } = useRooftop();
  const search = useRooftopSearchQuery;

  const columns = useMemo(
    () => [
      ...ROOFTOP_INDEX_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as const,
        flex: 1,
        align: 'right' as const,
        headerAlign: 'right' as const,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            showInMenu
            key={`view-${params.row.id}`}
            label="View"
            icon={<Visibility />}
            onClick={() => navigate(`/employer/rooftops/${params.row.id}`)}
          />,
          <GridActionsCellItem
            showInMenu
            key={`delete-${params.row.id}`}
            label="Delete"
            icon={<Delete color="error" />}
            onClick={() => deleteRooftop(params.row.id)}
          />,
        ],
      },
    ],
    [navigate, deleteRooftop],
  );

  const initialState = {
    columns: {
      columnVisibilityModel: {
        ...ROOFTOP_INDEX_COLUMN_INITIAL_STATE,
        authorizations: true,
        jobs: true,
      },
    },
  };

  return (
    <>
      <DashboardPageContainer helpContent={<RooftopHelpContent />} helpTitle="About Rooftops">
        <DataGrid<Rooftop>
          query={search}
          columns={columns}
          tools={
            <>
              <Button onClick={() => setOpen(true)} variant="contained" color="primary">
                Add
              </Button>
              <Divider orientation="vertical" sx={{ height: 24, ml: 1 }} />
            </>
          }
          initialState={initialState}
        />
      </DashboardPageContainer>
      <Drawer
        title="Add Rooftop"
        width={500}
        action={
          <InfoButton tooltip="What is a rooftop?" title="About Rooftops" color="primary.contrastText">
            <RooftopHelpContent />
          </InfoButton>
        }
      >
        <FormProvider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', p: 2 }}>
            <EmployerRooftopCreateForm />
          </Box>
        </FormProvider>
      </Drawer>
    </>
  );
};

export default EmployerRooftopIndex;
